/**
 * Vue Router Configuration for Numera Web
 * Handles multi-tenant routing with authentication guards
 */

import { createRouter, createWebHistory, type RouteLocationGeneric, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useTenantStore } from '@/stores/tenant'
import { lazyView, isDynamicImportChunkError, recoverFromChunkError } from '@/router/lazyView'
import { isTransientApiError } from '@/utils/transientApiError'
import { withBootDeadline, BOOT_DEADLINE_MS } from '@/utils/bootDeadline'
import type { User } from '@/types'

/** Absolute tenant-scoped path — relative redirect paths drop the tenant slug in Vue Router 4. */
const tenantPath = (suffix: string) => (to: RouteLocationGeneric) =>
  `/${String(to.params.tenantSlug)}/${suffix.replace(/^\//, '')}`

const tenantPathWithQuery = (suffix: string, extraQuery: Record<string, string>) =>
  (to: RouteLocationGeneric) => ({
    path: `/${String(to.params.tenantSlug)}/${suffix.replace(/^\//, '')}`,
    query: { ...to.query, ...extraQuery }
  })

// Lazy-loaded views
const Login = lazyView(() => import('@/views/auth/LoginView.vue'))
const Signup = lazyView(() => import('@/views/auth/SignupView.vue'))
const SignupVerify = lazyView(() => import('@/views/auth/SignupVerifyView.vue'))
const SignupCheckEmail = lazyView(() => import('@/views/auth/SignupCheckEmailView.vue'))
const ForgotPassword = lazyView(() => import('@/views/auth/ForgotPasswordView.vue'))
const ResetPassword = lazyView(() => import('@/views/auth/ResetPasswordView.vue'))
const TenantLayout = lazyView(() => import('@/components/layout/TenantLayout.vue'))
const Dashboard = lazyView(() => import('@/views/dashboard/DashboardView.vue'))
const NotFound = lazyView(() => import('@/views/NotFoundView.vue'))

// Accounting views
const ChartOfAccounts = lazyView(() => import('@/views/accounting/ChartOfAccountsView.vue'))
const JournalEntries = lazyView(() => import('@/views/accounting/JournalEntriesView.vue'))
const Estimates = lazyView(() => import('@/views/accounting/EstimatesView.vue'))
const Invoices = lazyView(() => import('@/views/accounting/InvoicesView.vue'))
const Bills = lazyView(() => import('@/views/accounting/BillsView.vue'))
const LandedCosts = lazyView(() => import('@/views/accounting/LandedCostsView.vue'))
const Payments = lazyView(() => import('@/views/accounting/PaymentsView.vue'))
const ReceivedPayments = lazyView(() => import('@/views/accounting/ReceivedPaymentsView.vue'))
const CustomerStatements = lazyView(() => import('@/views/accounting/CustomerStatementsView.vue'))
const Clients = lazyView(() => import('@/views/accounting/ClientsView.vue'))
const Vendors = lazyView(() => import('@/views/accounting/VendorsView.vue'))
const Items = lazyView(() => import('@/views/accounting/ItemsView.vue'))
const InventoryReports = lazyView(() => import('@/views/accounting/InventoryReportsView.vue'))
const InventoryLevels = lazyView(() => import('@/views/accounting/InventoryLevelsView.vue'))
const InventoryMovements = lazyView(() => import('@/views/accounting/InventoryMovementsView.vue'))
const PeriodClose = lazyView(() => import('@/views/accounting/PeriodCloseView.vue'))
const BankReconciliation = lazyView(() => import('@/views/accounting/BankReconciliationView.vue'))
const PurchaseOrders = lazyView(() => import('@/views/accounting/PurchaseOrdersView.vue'))
const Expenses = lazyView(() => import('@/views/accounting/ExpensesView.vue'))
const Checks = lazyView(() => import('@/views/accounting/ChecksView.vue'))
const CreditCardOps = lazyView(() => import('@/views/accounting/CreditCardOpsView.vue'))
const Recurring = lazyView(() => import('@/views/accounting/RecurringView.vue'))
const RecurringDetail = lazyView(() => import('@/views/accounting/RecurringDetailView.vue'))
const FixedAssets = lazyView(() => import('@/views/accounting/FixedAssetsView.vue'))
const Projects = lazyView(() => import('@/views/accounting/ProjectsView.vue'))
const Manufacturing = lazyView(() => import('@/views/manufacturing/ProductionOrdersView.vue'))
const Exports = lazyView(() => import('@/views/exports/ExportsView.vue'))
const TimeTracking = lazyView(() => import('@/views/time-tracking/TimeTrackingView.vue'))
const SalesOrders = lazyView(() => import('@/views/accounting/SalesOrdersView.vue'))
const CustomerAdvances = lazyView(() => import('@/views/accounting/CustomerAdvancesView.vue'))
const ExpenseClaims = lazyView(() => import('@/views/accounting/ExpenseClaimsView.vue'))
const SalesReceipts = lazyView(() => import('@/views/accounting/SalesReceiptsView.vue'))
const VendorCredits = lazyView(() => import('@/views/accounting/VendorCreditsView.vue'))
const BankDeposits = lazyView(() => import('@/views/accounting/BankDepositsView.vue'))
const RefundReceipts = lazyView(() => import('@/views/accounting/RefundReceiptsView.vue'))
const DelayedCharges = lazyView(() => import('@/views/accounting/DelayedChargesView.vue'))
const CreditNotes = lazyView(() => import('@/views/accounting/CreditNotesView.vue'))
const DebitNotes = lazyView(() => import('@/views/accounting/DebitNotesView.vue'))
const Transfers = lazyView(() => import('@/views/accounting/TransfersView.vue'))
const TaxWithholdings = lazyView(() => import('@/views/accounting/TaxWithholdingsView.vue'))
const Banking = lazyView(() => import('@/views/accounting/BankingView.vue'))
const TaxCenter = lazyView(() => import('@/views/accounting/TaxCenterView.vue'))
const Budgets = lazyView(() => import('@/views/accounting/BudgetsView.vue'))
const NCFManagement = lazyView(() => import('@/views/accounting/NCFManagementView.vue'))
const NotesTemplates = lazyView(() => import('@/views/accounting/NotesTemplatesView.vue'))

// User Management view
const UserManagement = lazyView(() => import('@/views/UserManagementView.vue'))

// User Profile and Settings views
const UserProfile = lazyView(() => import('@/views/UserProfileView.vue'))
const AccountSettings = lazyView(() => import('@/views/AccountSettingsView.vue'))
const CompanyInfo = lazyView(() => import('@/views/CompanyInfoView.vue'))

// Security views
const SecurityDashboard = lazyView(() => import('@/views/SecurityDashboardView.vue'))

// Reports views
const IncomeStatement = lazyView(() => import('@/views/reports/IncomeStatementView.vue'))
const BalanceSheet = lazyView(() => import('@/views/reports/BalanceSheetView.vue'))
const TrialBalance = lazyView(() => import('@/views/reports/TrialBalanceView.vue'))
const CashFlow = lazyView(() => import('@/views/reports/CashFlowView.vue'))
const ItbisReports = lazyView(() => import('@/views/reports/ItbisReportsView.vue'))
const ARAgingReport = lazyView(() => import('@/views/reports/ARAgingReportView.vue'))
const APAgingReport = lazyView(() => import('@/views/reports/APAgingReportView.vue'))
const SalesReports = lazyView(() => import('@/views/reports/SalesReportsView.vue'))
const PurchaseReports = lazyView(() => import('@/views/reports/PurchaseReportsView.vue'))
const InventoryValuation = lazyView(() => import('@/views/reports/InventoryValuationView.vue'))
const ItbisDetailReports = lazyView(() => import('@/views/reports/ItbisDetailReportsView.vue'))
// Payroll views
const PayrollDashboard = lazyView(() => import('@/views/payroll/PayrollDashboardView.vue'))
const PayrollEmployees = lazyView(() => import('@/views/payroll/EmployeesView.vue'))
const PayrollTimesheets = lazyView(() => import('@/views/payroll/PayrollTimesheetsView.vue'))
const PayrollTransactions = lazyView(() => import('@/views/payroll/PayrollTransactionsView.vue'))
const PayrollTaxes = lazyView(() => import('@/views/payroll/PayrollTaxesView.vue'))
const PayrollTaxForms = lazyView(() => import('@/views/payroll/TaxFormsView.vue'))

const ReportsHub = lazyView(() => import('@/views/reports/ReportsHubView.vue'))
const GeneralLedger = lazyView(() => import('@/views/reports/GeneralLedgerView.vue'))
const JournalReport = lazyView(() => import('@/views/reports/JournalReportView.vue'))
const IncomeStatementByPeriod = lazyView(() => import('@/views/reports/IncomeStatementByPeriodView.vue'))
const ComparativeBalanceSheet = lazyView(() => import('@/views/reports/ComparativeBalanceSheetView.vue'))

const routes: RouteRecordRaw[] = [
  // Root redirect to login
  {
    path: '/',
    redirect: '/login'
  },

  // Authentication routes
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: {
      requiresAuth: false,
      title: 'Iniciar sesión'
    }
  },

  // Self-service signup (plan §7) — public
  {
    path: '/signup',
    name: 'Signup',
    component: Signup,
    meta: { requiresAuth: false, title: 'Crear cuenta' }
  },
  {
    path: '/signup/verify',
    name: 'SignupVerify',
    component: SignupVerify,
    meta: { requiresAuth: false, title: 'Verificar cuenta' }
  },
  {
    path: '/signup/check-email',
    name: 'SignupCheckEmail',
    component: SignupCheckEmail,
    meta: { requiresAuth: false, title: 'Revisa tu correo' }
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: ForgotPassword,
    meta: { requiresAuth: false, title: 'Restablecer contraseña' }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: ResetPassword,
    meta: { requiresAuth: false, title: 'Nueva contraseña' }
  },

  // Multi-tenant routes
  {
    path: '/:tenantSlug',
    component: TenantLayout,
    meta: {
      requiresAuth: true,
      requiresTenant: true
    },
    children: [
      // Dashboard
      {
        path: '',
        name: 'Dashboard',
        component: Dashboard,
        meta: {
          title: 'Panel Principal',
          icon: 'HomeIcon',
          showInNav: true,
          order: 0
        }
      },

      // Legacy URL redirects (QA / bookmarks / Spanish slugs)
      { path: 'inventory', redirect: { name: 'InventoryLevels' } },
      { path: 'inventory/stock', redirect: { name: 'InventoryLevels' } },
      { path: 'inventario', redirect: { name: 'InventoryLevels' } },
      { path: 'inventario/existencias', redirect: { name: 'InventoryLevels' } },
      { path: 'payroll', redirect: { name: 'PayrollDashboard' } },
      { path: 'settings', redirect: { name: 'AccountSettings' } },
      { path: 'invoices/sales', redirect: { name: 'Invoices' } },
      { path: 'invoices/new', redirect: { name: 'Invoices', query: { action: 'create' } } },
      { path: 'bank', redirect: { name: 'Banking' } },
      { path: 'banca', redirect: { name: 'Banking' } },
      { path: 'tax', redirect: { name: 'TaxCenter' } },
      { path: 'taxes', redirect: { name: 'TaxCenter' } },
      { path: 'impuestos', redirect: { name: 'TaxCenter' } },
      { path: 'products', redirect: { name: 'Items' } },
      { path: 'productos', redirect: { name: 'Items' } },
      { path: 'products-services', redirect: { name: 'Items' } },
      { path: 'payroll/runs', redirect: { name: 'PayrollTransactions' } },
      { path: 'payroll/run-history', redirect: { name: 'PayrollTransactions' } },
      { path: 'security/dashboard', redirect: tenantPath('security') },
      { path: 'quotes', redirect: tenantPath('estimates') },
      {
        path: 'quotes/:quoteRef',
        redirect: (to) => `/${String(to.params.tenantSlug)}/estimates/${String(to.params.quoteRef)}`
      },
      { path: 'cotizaciones', redirect: tenantPath('estimates') },
      {
        path: 'cotizaciones/:quoteRef',
        redirect: (to) => `/${String(to.params.tenantSlug)}/estimates/${String(to.params.quoteRef)}`
      },
      { path: 'accounting/accounts', redirect: { name: 'ChartOfAccounts' } },
      { path: 'chart-of-accounts', redirect: { name: 'ChartOfAccounts' } },
      { path: 'invoices/create', redirect: { name: 'Invoices', query: { action: 'create' } } },
      { path: 'invoices/new', redirect: { name: 'Invoices', query: { action: 'create' } } },
      { path: 'bills/create', redirect: { name: 'Bills', query: { action: 'create' } } },
      { path: 'bills/new', redirect: { name: 'Bills', query: { action: 'create' } } },
      { path: 'payments/create', redirect: { name: 'Payments', query: { action: 'create' } } },
      { path: 'payments/new', redirect: { name: 'Payments', query: { action: 'create' } } },
      { path: 'items/create', redirect: { name: 'Items', query: { action: 'create' } } },
      { path: 'items/new', redirect: { name: 'Items', query: { action: 'create' } } },
      { path: 'clients/create', redirect: { name: 'Clients', query: { action: 'create' } } },
      { path: 'expenses/create', redirect: { name: 'Expenses', query: { action: 'create' } } },
      { path: 'settings/users', redirect: { name: 'UserManagement' } },
      { path: 'user-management', redirect: { name: 'UserManagement' } },
      // Round 2 QA — bookmarks / nested paths not matching canonical routes
      { path: 'bank-rules', redirect: { name: 'Banking', query: { tab: 'rules' } } },
      { path: 'banking/rules', redirect: { name: 'Banking', query: { tab: 'rules' } } },
      { path: 'credit-notes/create', redirect: tenantPathWithQuery('credit-notes', { action: 'create' }) },
      { path: 'credit-notes/new', redirect: tenantPathWithQuery('credit-notes', { action: 'create' }) },
      { path: 'debit-notes/create', redirect: tenantPathWithQuery('debit-notes', { action: 'create' }) },
      { path: 'debit-notes/new', redirect: tenantPathWithQuery('debit-notes', { action: 'create' }) },
      { path: 'delayed-charges/create', redirect: tenantPathWithQuery('delayed-charges', { action: 'create' }) },
      { path: 'delayed-charges/new', redirect: tenantPathWithQuery('delayed-charges', { action: 'create' }) },
      { path: 'inventory/movimientos', redirect: tenantPath('inventory/stock-movements') },
      { path: 'inventory/valuation/fifo-lifo', redirect: tenantPath('inventory-reports') },
      { path: 'inventory/valuation', redirect: tenantPath('inventory-reports') },
      { path: 'ventas/recibos', redirect: { name: 'SalesReceipts' } },
      { path: 'recibos-venta', redirect: { name: 'SalesReceipts' } },
      { path: 'sales-invoices-recurring', redirect: tenantPathWithQuery('recurring', { templateType: 'invoice' }) },
      // QA bookmarks — English/legacy slugs → canonical Spanish routes
      { path: 'suppliers', redirect: { name: 'Vendors' } },
      { path: 'proveedores', redirect: { name: 'Vendors' } },
      { path: 'vendor-expense-claims', redirect: { name: 'ExpenseClaims' } },
      { path: 'gastos', redirect: { name: 'Expenses' } },
      { path: 'banca/depositos', redirect: { name: 'BankDeposits' } },
      { path: 'banking/deposits', redirect: { name: 'BankDeposits' } },
      // Round 3 QA — detail routes, report aliases, statement create paths
      { path: 'payroll/tax', redirect: tenantPath('payroll/taxes') },
      { path: 'customers/statements', redirect: tenantPath('customer-statements') },
      { path: 'reports/aged-payables', redirect: tenantPath('reports/ap-aging') },
      { path: 'inventory/items', redirect: { name: 'Items' } },

      // Canonical paths → QA bookmark URLs (single source of truth for round 8–10 routes)
      { path: 'inventory-movements', redirect: tenantPath('inventory/stock-movements') },
      { path: 'reports/cash-flow', redirect: tenantPath('reports/cash-basis') },
      { path: 'reports/ar-aging', redirect: tenantPath('reports/aged-receivables') },
      { path: 'customer-statements/generate', redirect: tenantPath('customers/statements/new') },
      { path: 'bank-reconciliation', redirect: tenantPath('banking/reconciliation') },
      // c672: /bank-accounts era una URL de marcador histórica y caía al 404.
      // Las cuentas bancarias se administran dentro de la vista de conciliación.
      { path: 'bank-accounts', redirect: tenantPath('banking/reconciliation') },

      // Round 11 QA (#31/#32) — bookmark URLs that fell through to the 404 view.
      // The views already exist under canonical paths; these map the QA URLs onto them.
      { path: 'clients/new', redirect: tenantPathWithQuery('clients', { action: 'create' }) },
      { path: 'expenses/new', redirect: tenantPathWithQuery('expenses', { action: 'create' }) },
      { path: 'recurring-templates', redirect: tenantPath('recurring') },
      { path: 'accounts-catalog', redirect: { name: 'ChartOfAccounts' } },
      { path: 'bank-transfers', redirect: { name: 'Transfers' } },
      { path: 'import-costing', redirect: { name: 'LandedCosts' } },
      { path: 'inventory-valuation', redirect: tenantPath('reports/inventory-valuation') },
      { path: 'period-closes', redirect: { name: 'PeriodClose' } },
      { path: 'payroll/runs/new', redirect: tenantPathWithQuery('payroll/transactions', { action: 'create' }) },
      { path: 'admin/users', redirect: { name: 'UserManagement' } },
      { path: 'admin/roles', redirect: { name: 'UserManagement' } },
      { path: 'audit-logs', redirect: { name: 'SecurityDashboard' } },
      { path: 'tax-declaration', redirect: { name: 'TaxCenter' } },
      { path: 'journal-entries', redirect: { name: 'JournalEntries' } },
      { path: 'admin/periods', redirect: { name: 'PeriodClose' } },

      // Round 8 QA — legacy bookmark URLs as first-class routes (not redirect-only)
      {
        path: 'inventory/stock-movements',
        name: 'InventoryMovements',
        component: InventoryMovements,
        meta: {
          title: 'Movimientos de Inventario',
          section: 'inventario',
          showInNav: false
        }
      },
      {
        path: 'reports/cash-basis',
        name: 'CashFlow',
        component: CashFlow,
        meta: {
          title: 'Estado de Flujo de Efectivo',
          section: 'informes',
          showInNav: false
        }
      },
      {
        path: 'reports/aged-receivables',
        name: 'ARAgingReport',
        component: ARAgingReport,
        meta: {
          title: 'Antigüedad de Cuentas por Cobrar',
          section: 'informes',
          showInNav: false
        }
      },
      {
        path: 'customers/statements/new',
        name: 'CustomerStatementGenerate',
        component: CustomerStatements,
        meta: {
          title: 'Generar Estado de Cuenta',
          section: 'ventas',
          showInNav: false,
          statementView: 'generate'
        }
      },
      {
        path: 'settings/appearance',
        name: 'AppearanceSettings',
        component: UserProfile,
        meta: {
          title: 'Apariencia',
          showInNav: false,
          appearanceSection: true
        }
      },
      {
        path: 'payroll/tax/new',
        name: 'PayrollTaxNew',
        component: PayrollTaxes,
        meta: {
          title: 'Impuestos de Nómina',
          section: 'nomina',
          showInNav: false,
          minRole: 'accountant' as const
        }
      },
      {
        path: 'banking/reconciliation',
        name: 'BankReconciliation',
        component: BankReconciliation,
        meta: {
          title: 'Conciliación bancaria',
          section: 'banca',
          showInNav: false
        }
      },
      {
        path: 'supplier-payments',
        name: 'SupplierPayments',
        component: Payments,
        meta: {
          title: 'Pagos a Proveedores',
          section: 'gastos',
          showInNav: false,
          supplierPayments: true
        }
      },
      { path: 'credit-memos', redirect: tenantPath('credit-notes') },
      { path: 'debit-memos', redirect: tenantPath('debit-notes') },

      // Ventas (Sales) - USO MÁS FRECUENTE
      {
        path: 'estimates',
        name: 'Estimates',
        component: Estimates,
        meta: {
          title: 'Estimaciones',
          icon: 'DocumentTextIcon',
          showInNav: true,
          section: 'ventas',
          order: 1
        }
      },
      {
        path: 'sales-orders',
        name: 'SalesOrders',
        component: SalesOrders,
        meta: {
          title: 'Órdenes de Venta',
          icon: 'ClipboardDocumentCheckIcon',
          showInNav: true,
          section: 'ventas',
          order: 2
        }
      },
      {
        path: 'customer-advances',
        name: 'CustomerAdvances',
        component: CustomerAdvances,
        meta: {
          title: 'Adelantos de Clientes',
          icon: 'BanknotesIcon',
          showInNav: true,
          section: 'ventas',
          order: 5
        }
      },
      {
        path: 'sales-receipts',
        name: 'SalesReceipts',
        component: SalesReceipts,
        meta: {
          title: 'Recibos de venta',
          icon: 'ReceiptPercentIcon',
          showInNav: true,
          section: 'ventas',
          order: 5
        }
      },
      {
        path: 'refund-receipts',
        name: 'RefundReceipts',
        component: RefundReceipts,
        meta: {
          title: 'Recibos de reembolso',
          icon: 'ReceiptRefundIcon',
          showInNav: true,
          section: 'ventas',
          order: 5
        }
      },
      {
        path: 'delayed-charges',
        name: 'DelayedCharges',
        component: DelayedCharges,
        meta: {
          title: 'Cobros y Créditos Diferidos',
          icon: 'ClockIcon',
          showInNav: true,
          section: 'ventas',
          order: 5
        }
      },
      {
        path: 'credit-notes',
        name: 'CreditNotes',
        component: CreditNotes,
        meta: {
          title: 'Notas de Crédito',
          createTitle: 'Nueva Nota de Crédito',
          icon: 'DocumentMinusIcon',
          showInNav: true,
          section: 'ventas',
          order: 6
        }
      },
      {
        path: 'credit-notes/:creditNoteRef',
        name: 'CreditNoteDetail',
        component: CreditNotes,
        meta: {
          title: 'Detalle de Nota de Crédito',
          showInNav: false,
          section: 'ventas'
        }
      },
      {
        path: 'credit-memos/:creditMemoRef',
        name: 'CreditMemoDetail',
        component: CreditNotes,
        meta: {
          title: 'Detalle de Nota de Crédito',
          showInNav: false,
          section: 'ventas'
        }
      },
      {
        path: 'debit-notes',
        name: 'DebitNotes',
        component: DebitNotes,
        meta: {
          title: 'Notas de Débito',
          createTitle: 'Nueva Nota de Débito',
          icon: 'DocumentPlusIcon',
          showInNav: true,
          section: 'ventas',
          order: 7
        }
      },
      {
        path: 'debit-notes/:debitNoteRef',
        name: 'DebitNoteDetail',
        component: DebitNotes,
        meta: {
          title: 'Detalle de Nota de Débito',
          showInNav: false,
          section: 'ventas'
        }
      },
      {
        path: 'debit-memos/:debitMemoRef',
        name: 'DebitMemoDetail',
        component: DebitNotes,
        meta: {
          title: 'Detalle de Nota de Débito',
          showInNav: false,
          section: 'ventas'
        }
      },
      {
        path: 'estimates/:estimateId?',
        name: 'EstimateDetail',
        component: Estimates,
        meta: {
          title: 'Estimaciones',
          showInNav: false
        }
      },
      {
        path: 'invoices',
        name: 'Invoices',
        component: Invoices,
        meta: {
          title: 'Facturas',
          createTitle: 'Nueva Factura',
          icon: 'DocumentIcon',
          showInNav: true,
          section: 'ventas',
          order: 2
        }
      },
      {
        path: 'invoices/:invoiceRef',
        name: 'InvoiceDetail',
        component: Invoices,
        beforeEnter: (to, _from, next) => {
          const ref = String(to.params.invoiceRef || '').toLowerCase()
          if (ref === 'create' || ref === 'new') {
            next({ name: 'Invoices', params: { tenantSlug: to.params.tenantSlug }, query: { action: 'create' } })
            return
          }
          next()
        },
        meta: {
          title: 'Detalle de Factura',
          showInNav: false,
          section: 'ventas'
        }
      },
      {
        path: 'notes-templates',
        name: 'NotesTemplates',
        component: NotesTemplates,
        meta: {
          title: 'Plantillas de Notas',
          icon: 'DocumentTextIcon',
          showInNav: false,
          section: 'ventas',
          order: 20
        }
      },
      {
        path: 'invoice-templates',
        name: 'InvoiceTemplates',
        component: NotesTemplates,
        meta: {
          title: 'Plantillas de Facturas',
          icon: 'DocumentTextIcon',
          showInNav: false,
          section: 'ventas',
          order: 20
        }
      },

      // Clientes y Proveedores (Clients and Vendors) - NECESARIOS PARA OPERACIONES DIARIAS
      {
        path: 'clients',
        name: 'Clients',
        component: Clients,
        meta: {
          title: 'Clientes',
          createTitle: 'Nuevo Cliente',
          icon: 'UserGroupIcon',
          showInNav: true,
          section: 'clientes',
          order: 2
        }
      },
      {
        path: 'items',
        name: 'Items',
        component: Items,
        meta: {
          title: 'Productos y Servicios',
          createTitle: 'Nuevo Producto',
          icon: 'CubeIcon',
          showInNav: true,
          section: 'ventas',
          order: 3
        }
      },

      // Gastos (Expenses) - USO FRECUENTE
      {
        path: 'expenses',
        name: 'Expenses',
        component: Expenses,
        meta: {
          title: 'Gastos',
          createTitle: 'Nuevo Gasto',
          icon: 'BanknotesIcon',
          showInNav: true,
          section: 'gastos',
          order: 3
        }
      },
      {
        path: 'checks',
        name: 'Checks',
        component: Checks,
        meta: {
          title: 'Cheques',
          icon: 'DocumentCheckIcon',
          showInNav: true,
          section: 'gastos',
          order: 4
        }
      },
      {
        path: 'credit-card',
        name: 'CreditCardOps',
        component: CreditCardOps,
        meta: {
          title: 'Tarjeta de Crédito',
          icon: 'CreditCardIcon',
          showInNav: true,
          section: 'gastos',
          order: 5
        }
      },
      {
        path: 'credit-cards',
        name: 'CreditCards',
        component: CreditCardOps,
        meta: {
          title: 'Tarjetas de Crédito',
          icon: 'CreditCardIcon',
          showInNav: false,
          section: 'gastos',
          order: 5
        }
      },
      {
        path: 'recurring',
        name: 'Recurring',
        component: Recurring,
        meta: {
          title: 'Transacciones recurrentes',
          icon: 'ArrowPathIcon',
          showInNav: true,
          section: 'gastos',
          order: 6
        }
      },
      {
        path: 'recurring/:templateId',
        name: 'RecurringDetail',
        component: RecurringDetail,
        meta: {
          title: 'Detalle de plantilla recurrente',
          showInNav: false,
          section: 'gastos'
        }
      },
      {
        path: 'expense-claims',
        name: 'ExpenseClaims',
        component: ExpenseClaims,
        meta: {
          title: 'Reclamos de Gastos',
          icon: 'ReceiptPercentIcon',
          showInNav: true,
          section: 'gastos',
          order: 7
        }
      },
      {
        path: 'vendor-credits',
        name: 'VendorCredits',
        component: VendorCredits,
        meta: {
          title: 'Créditos de proveedor',
          icon: 'DocumentMinusIcon',
          showInNav: true,
          section: 'gastos',
          order: 8
        }
      },
      {
        path: 'bills',
        name: 'Bills',
        component: Bills,
        meta: {
          title: 'Facturas de compra',
          createTitle: 'Nueva Factura de Compra',
          icon: 'ReceiptRefundIcon',
          showInNav: true,
          section: 'gastos',
          order: 4
        }
      },
      {
        path: 'landed-costs',
        name: 'LandedCosts',
        component: LandedCosts,
        meta: {
          title: 'Costeo de Importaciones',
          icon: 'CubeIcon',
          showInNav: true,
          section: 'gastos',
          order: 6
        }
      },
      {
        path: 'purchase-orders',
        name: 'PurchaseOrders',
        component: PurchaseOrders,
        meta: {
          title: 'Órdenes de Compra',
          createTitle: 'Nueva Orden de Compra',
          icon: 'ClipboardDocumentListIcon',
          showInNav: true,
          section: 'gastos',
          order: 5
        }
      },
      {
        path: 'purchase-orders/:orderRef',
        name: 'PurchaseOrderDetail',
        component: PurchaseOrders,
        beforeEnter: (to, _from, next) => {
          const ref = String(to.params.orderRef || '').toLowerCase()
          if (ref === 'create' || ref === 'new') {
            next({ name: 'PurchaseOrders', params: { tenantSlug: to.params.tenantSlug }, query: { action: 'create' } })
            return
          }
          next()
        },
        meta: {
          title: 'Detalle de Orden de Compra',
          showInNav: false,
          section: 'gastos'
        }
      },
      {
        path: 'vendors',
        name: 'Vendors',
        component: Vendors,
        meta: {
          title: 'Proveedores',
          icon: 'BuildingOfficeIcon',
          showInNav: true,
          section: 'clientes',
          order: 5
        }
      },
      {
        path: 'payments',
        name: 'Payments',
        component: Payments,
        meta: {
          title: 'Pagos',
          createTitle: 'Nuevo Pago',
          icon: 'CreditCardIcon',
          showInNav: true,
          section: 'gastos',
          order: 6
        }
      },
      {
        path: 'recurring-invoices',
        name: 'RecurringInvoices',
        redirect: (to) => ({
          name: 'Recurring',
          params: to.params,
          query: { ...to.query, templateType: 'invoice' }
        }),
        meta: {
          title: 'Facturas Recurrentes',
          section: 'ventas'
        }
      },
      {
        path: 'received-payments',
        name: 'ReceivedPayments',
        component: ReceivedPayments,
        meta: {
          title: 'Pagos Recibidos',
          icon: 'BanknotesIcon',
          showInNav: true,
          section: 'ventas',
          order: 8
        }
      },
      {
        path: 'received-payments/:paymentId?',
        name: 'ReceivedPaymentDetail',
        component: ReceivedPayments,
        meta: {
          title: 'Detalle de Pago Recibido',
          showInNav: false
        }
      },
      {
        path: 'customer-statements',
        name: 'CustomerStatements',
        component: CustomerStatements,
        meta: {
          title: 'Estados de Cuenta',
          icon: 'DocumentDuplicateIcon',
          showInNav: true,
          section: 'ventas',
          order: 9
        }
      },
      {
        path: 'customer-statements/generate/batch',
        name: 'CustomerStatementBatchGenerate',
        component: CustomerStatements,
        meta: {
          title: 'Generar Estados de Cuenta',
          showInNav: false,
          section: 'ventas'
        }
      },
      {
        path: 'customer-statements/:statementId',
        name: 'CustomerStatementDetail',
        component: CustomerStatements,
        meta: {
          title: 'Detalle de Estado de Cuenta',
          showInNav: false
        }
      },

      // Informes (Reports) - USO MODERADO PERO IMPORTANTE
      {
        path: 'reports',
        name: 'ReportsHub',
        component: ReportsHub,
        meta: {
          title: 'Informes',
          icon: 'ChartBarSquareIcon',
          showInNav: false,
          section: 'informes',
          order: 5
        }
      },
      {
        path: 'reports/general-ledger',
        name: 'GeneralLedger',
        component: GeneralLedger,
        meta: {
          title: 'Libro Mayor',
          icon: 'BookOpenIcon',
          showInNav: true,
          section: 'informes',
          order: 5
        }
      },
      {
        path: 'reports/journal',
        name: 'JournalReport',
        component: JournalReport,
        meta: {
          title: 'Diario General',
          icon: 'DocumentTextIcon',
          showInNav: true,
          section: 'informes',
          order: 5
        }
      },
      {
        path: 'reports/income-statement-by-period',
        name: 'IncomeStatementByPeriod',
        component: IncomeStatementByPeriod,
        meta: {
          title: 'Estado de Resultados por Período',
          icon: 'ChartBarIcon',
          showInNav: true,
          section: 'informes',
          order: 9
        }
      },
      {
        path: 'reports/balance-sheet-comparative',
        name: 'ComparativeBalanceSheet',
        component: ComparativeBalanceSheet,
        meta: {
          title: 'Balance General Comparativo',
          icon: 'ScaleIcon',
          showInNav: true,
          section: 'informes',
          order: 10
        }
      },
      {
        path: 'reports/ap-aging',
        name: 'APAgingReport',
        component: APAgingReport,
        meta: {
          title: 'Antigüedad de Cuentas por Pagar',
          icon: 'ClockIcon',
          showInNav: true,
          section: 'informes',
          order: 7
        }
      },
      {
        path: 'reports/income-statement',
        name: 'IncomeStatement',
        component: IncomeStatement,
        meta: {
          title: 'Estado de Resultados',
          icon: 'ChartBarIcon',
          showInNav: true,
          section: 'informes',
          order: 8
        }
      },
      {
        path: 'reports/balance-sheet',
        name: 'BalanceSheet',
        component: BalanceSheet,
        meta: {
          title: 'Balance General',
          icon: 'ScaleIcon',
          showInNav: true,
          section: 'informes',
          order: 9
        }
      },
      {
        path: 'inventory-levels',
        name: 'InventoryLevels',
        component: InventoryLevels,
        meta: {
          title: 'Existencias',
          icon: 'CubeIcon',
          showInNav: true,
          section: 'inventario',
          order: 1
        }
      },
      {
        path: 'inventory-reports',
        name: 'InventoryReports',
        component: InventoryReports,
        meta: {
          title: 'Valoración (métodos)',
          icon: 'ChartBarIcon',
          showInNav: true,
          section: 'inventario',
          order: 3
        }
      },

      // Transacciones (Transactions) - USO ADMINISTRATIVO/BAJO
      {
        path: 'journal',
        name: 'JournalEntries',
        component: JournalEntries,
        meta: {
          title: 'Asientos Contables',
          icon: 'DocumentTextIcon',
          showInNav: true,
          section: 'transacciones',
          order: 12
        }
      },
      {
        path: 'accounts',
        name: 'ChartOfAccounts',
        component: ChartOfAccounts,
        meta: {
          title: 'Catálogo de cuentas',
          icon: 'ListBulletIcon',
          showInNav: true,
          section: 'transacciones',
          order: 13
        }
      },
      {
        path: 'reports/trial-balance',
        name: 'TrialBalance',
        component: TrialBalance,
        meta: {
          title: 'Balance de Comprobación',
          icon: 'DocumentTextIcon',
          showInNav: true,
          section: 'informes',
          order: 14
        }
      },
      {
        path: 'bank-deposits',
        name: 'BankDeposits',
        component: BankDeposits,
        meta: {
          title: 'Depósitos Bancarios',
          icon: 'BanknotesIcon',
          showInNav: true,
          section: 'transacciones',
          order: 15
        }
      },

      {
        path: 'fixed-assets',
        name: 'FixedAssets',
        component: FixedAssets,
        meta: {
          title: 'Activos fijos',
          icon: 'BuildingOfficeIcon',
          showInNav: true,
          section: 'transacciones',
          order: 16
        }
      },
      {
        path: 'transfers',
        name: 'Transfers',
        component: Transfers,
        meta: {
          title: 'Transferencias',
          icon: 'ArrowsRightLeftIcon',
          showInNav: true,
          section: 'transacciones',
          order: 17
        }
      },

      // ===== Project Costing Module =====
      {
        path: 'projects',
        name: 'Projects',
        component: Projects,
        meta: {
          title: 'Proyectos',
          icon: 'BriefcaseIcon',
          showInNav: true,
          section: 'proyectos',
          order: 10,
          feature: 'projects'
        }
      },
      {
        path: 'projects/:id',
        name: 'ProjectDetail',
        component: Projects,
        meta: {
          title: 'Detalle de Proyecto',
          showInNav: false,
          section: 'proyectos',
          feature: 'projects'
        }
      },

      // ===== Manufacturing / Production Module (feature: production) =====
      {
        path: 'manufacturing',
        name: 'Manufacturing',
        component: Manufacturing,
        meta: {
          title: 'Producción',
          icon: 'CogIcon',
          showInNav: true,
          section: 'produccion',
          feature: 'production',
          order: 10
        }
      },

      // ===== Export & Customs Module (feature: exportDocs) =====
      {
        path: 'exports',
        name: 'Exports',
        component: Exports,
        meta: {
          title: 'Exportación',
          icon: 'GlobeIcon',
          showInNav: true,
          section: 'exportacion',
          feature: 'exportDocs',
          order: 10
        }
      },

      // ===== Time Tracking Module (feature: timeTracking) =====
      {
        path: 'time-tracking',
        name: 'TimeTracking',
        component: TimeTracking,
        meta: {
          title: 'Registro de Tiempo',
          icon: 'ClockIcon',
          showInNav: true,
          section: 'tiempo',
          feature: 'timeTracking',
          order: 10
        }
      },

      // Management Reports - Phase 4
      {
        path: 'reports/sales',
        name: 'SalesReports',
        component: SalesReports,
        meta: {
          title: 'Reportes de Ventas',
          icon: 'ChartBarIcon',
          showInNav: true,
          section: 'informes',
          order: 20
        }
      },
      {
        path: 'reports/purchases',
        name: 'PurchaseReports',
        component: PurchaseReports,
        meta: {
          title: 'Reportes de Compras',
          icon: 'ChartBarIcon',
          showInNav: true,
          section: 'informes',
          order: 21
        }
      },
      {
        path: 'reports/inventory-valuation',
        name: 'InventoryValuation',
        component: InventoryValuation,
        meta: {
          title: 'Valoración de Inventario',
          icon: 'CubeIcon',
          showInNav: true,
          section: 'informes',
          order: 22
        }
      },
      {
        path: 'reports/itbis-detail',
        name: 'ItbisDetailReports',
        component: ItbisDetailReports,
        meta: {
          title: 'Reportes Detallados de ITBIS',
          icon: 'CalculatorIcon',
          showInNav: true,
          section: 'impuestos',
          order: 23
        }
      },

      // Impuestos (Taxes) - USO ESPECÍFICO/PERIÓDICO
      {
        path: 'reports/itbis',
        name: 'ItbisReports',
        component: ItbisReports,
        meta: {
          title: 'Reporte de ITBIS',
          icon: 'CalculatorIcon',
          showInNav: true,
          section: 'impuestos',
          countrySpecific: 'DO',
          order: 16
        }
      },
      {
        path: 'tax-withholdings',
        name: 'TaxWithholdings',
        component: TaxWithholdings,
        meta: {
          title: 'Retenciones de impuestos',
          icon: 'ReceiptPercentIcon',
          showInNav: true,
          section: 'impuestos',
          order: 17
        }
      },

      {
        path: 'ncf',
        name: 'NCFManagement',
        component: NCFManagement,
        meta: {
          title: 'Comprobantes Fiscales (NCF)',
          icon: 'DocumentTextIcon',
          showInNav: true,
          section: 'impuestos',
          countrySpecific: 'DO',
          minRole: 'admin' as const,
          order: 24
        }
      },

      {
        path: 'payroll/dashboard',
        name: 'PayrollDashboard',
        component: PayrollDashboard,
        meta: {
          title: 'Panel de nómina',
          icon: 'BanknotesIcon',
          showInNav: false,
          section: 'nomina',
          minRole: 'accountant' as const,
          order: 30
        }
      },
      {
        path: 'payroll/employees',
        name: 'Employees',
        component: PayrollEmployees,
        meta: {
          title: 'Empleados',
          icon: 'UsersIcon',
          showInNav: false,
          section: 'nomina',
          minRole: 'accountant' as const,
          order: 31
        }
      },
      {
        path: 'payroll/employees/:employeeRef',
        name: 'EmployeeDetail',
        component: PayrollEmployees,
        meta: {
          title: 'Detalle de Empleado',
          showInNav: false,
          section: 'nomina',
          minRole: 'accountant' as const
        }
      },
      {
        path: 'payroll/timesheets',
        name: 'PayrollTimesheets',
        component: PayrollTimesheets,
        meta: {
          title: 'Hojas de Tiempo',
          icon: 'ClockIcon',
          showInNav: false,
          section: 'nomina',
          minRole: 'accountant' as const,
          order: 32
        }
      },
      {
        path: 'payroll/transactions',
        name: 'PayrollTransactions',
        component: PayrollTransactions,
        meta: {
          title: 'Transacciones de Nómina',
          icon: 'DocumentTextIcon',
          showInNav: false,
          section: 'nomina',
          minRole: 'accountant' as const,
          order: 33
        }
      },
      {
        path: 'payroll/taxes',
        name: 'PayrollTaxes',
        component: PayrollTaxes,
        meta: {
          title: 'Impuestos de Nómina',
          icon: 'CalculatorIcon',
          showInNav: false,
          section: 'nomina',
          minRole: 'accountant' as const,
          order: 34
        }
      },
      {
        path: 'payroll/tax-forms',
        name: 'TaxForms',
        component: PayrollTaxForms,
        meta: {
          title: 'Formularios Fiscales',
          icon: 'DocumentDuplicateIcon',
          showInNav: false,
          section: 'nomina',
          minRole: 'accountant' as const,
          order: 35
        }
      },

      // Banca, Impuestos Centro, Presupuestos
      {
        path: 'banking',
        name: 'Banking',
        component: Banking,
        meta: {
          title: 'Banca',
          icon: 'BuildingLibraryIcon',
          showInNav: true,
          section: 'banca',
          order: 18
        }
      },
      {
        path: 'tax-center',
        name: 'TaxCenter',
        component: TaxCenter,
        meta: {
          title: 'Centro de Impuestos',
          icon: 'CalculatorIcon',
          showInNav: true,
          section: 'impuestos',
          order: 18
        }
      },
      {
        path: 'budgets',
        name: 'Budgets',
        component: Budgets,
        meta: {
          title: 'Presupuestos',
          icon: 'ChartBarIcon',
          showInNav: true,
          section: 'presupuestos',
          order: 18
        }
      },

      // Presupuestos (Budgets/Period Management) - USO ESPORÁDICO
      {
        path: 'period-close',
        name: 'PeriodClose',
        component: PeriodClose,
        meta: {
          title: 'Cierre de Período',
          icon: 'CalendarIcon',
          showInNav: true,
          section: 'presupuestos',
          minRole: 'admin' as const,
          order: 17
        }
      },

      {
        path: 'users',
        name: 'UserManagement',
        component: UserManagement,
        meta: {
          title: 'Gestión de usuarios',
          icon: 'UserIcon',
          showInNav: true,
          section: 'empleados',
          requiresAdmin: true,
          minRole: 'admin' as const,
          order: 18
        }
      },

      // User Profile and Settings (not shown in navigation)
      {
        path: 'profile',
        name: 'UserProfile',
        component: UserProfile,
        meta: {
          title: 'Mi Perfil',
          showInNav: false
        }
      },
      {
        path: 'account-settings',
        name: 'AccountSettings',
        component: AccountSettings,
        meta: {
          title: 'Configuración de cuenta',
          showInNav: false
        }
      },
      {
        path: 'company-settings',
        name: 'CompanyInfo',
        component: CompanyInfo,
        meta: {
          title: 'Información de la Empresa',
          showInNav: false
        }
      },
      { path: 'company', redirect: { name: 'CompanyInfo' } },

      {
        path: 'security',
        name: 'SecurityDashboard',
        component: SecurityDashboard,
        meta: {
          title: 'Centro de Seguridad',
          icon: 'ShieldIcon',
          showInNav: true,
          section: 'seguridad',
          requiresAdmin: true,
          minRole: 'admin' as const,
          order: 19
        }
      },

      // Un enlace que ya no existe —una factura borrada, una ruta renombrada,
      // una URL mal escrita— tiene que caer AQUÍ y no en el 404 global. Sin
      // este hijo, `/:tenantSlug/<cualquier cosa>` se escapaba del shell y
      // aterrizaba en el 404 pelado: medido en vivo (c784) con
      // `uxqad1582/facturas/999999` en 1440×900 y 390×844, la página dejaba
      // 0 enlaces de navegación, ninguna cabecera y ningún <main>, o sea el
      // usuario perdía el sidebar, los favoritos y hasta el nombre de su
      // empresa. Con el hijo, el shell sobrevive y el 404 aparece dentro de
      // él: se sigue trabajando en vez de quedarse encerrado.
      // Tiene que ser el ÚLTIMO hijo: en Vue Router gana el primero que
      // hace match, así que cualquier ruta real puesta después nunca se
      // alcanzaría.
      {
        path: ':pathMatch(.*)*',
        name: 'TenantNotFound',
        component: NotFound,
        meta: {
          title: 'Página No Encontrada'
        }
      }
    ]
  },

  // 404 Not Found
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFound,
    meta: {
      title: 'Página No Encontrada'
    }
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  }
})

router.onError((error, to) => {
  if (isDynamicImportChunkError(error)) {
    recoverFromChunkError(to.fullPath)
  }
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  const tenantStore = useTenantStore()

  // Initialize auth store if not already done. Acotado con withBootDeadline:
  // sin límite, cada intento podía bloquear la navegación hasta el timeout de
  // axios (30s) — o colgar indefinidamente si la conexión se estanca — y dejar
  // la SPA en blanco. El BootDeadlineError es TRANSITORIO para
  // isTransientApiError(), así que cae en el mismo camino degradado.
  if (authStore.token && !authStore.user) {
    try {
      await withBootDeadline(authStore.initialize(), BOOT_DEADLINE_MS, 'initialize')
    } catch (error) {
      console.warn('Auth initialization failed:', error)
      // Only redirect if it's a critical error or authentication failed
      // For other errors, let the user try to navigate normally
      if (!authStore.isAuthenticated && !authStore.token) {
        next('/login')
        return
      }
      // El plazo venció (o la red falló) mientras el restore seguía en vuelo:
      // el initialize() original todavía no marcó la sesión degradada — lo
      // hará cuando su propia petición muera — pero el requiresAuth check de
      // abajo se ejecuta YA y sin el flag expulsaría un token válido a
      // /login. Marco el flag ahora; initialize() lo corregirá al aterrizar.
      if (authStore.token && isTransientApiError(error)) {
        authStore.markSessionDegraded()
      }
    }
  }

  // Check authentication requirements
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    // Token present but the profile restore hit a transient (non-auth) error:
    // keep the user inside the app — views render their own error/retry
    // states — instead of ejecting a valid session to /login. The auth store
    // retries the restore in the background and on the next navigation.
    if (authStore.token && authStore.sessionRestoreDegraded) {
      next()
      return
    }
    next('/login')
    return
  }

  // Enforce role-based access control
  if (to.meta.minRole && !authStore.canAccess(to.meta.minRole as User['role'])) {
    const tenantSlug = to.params.tenantSlug as string
    if (tenantSlug) {
      next({ name: 'Dashboard', params: { tenantSlug } })
    } else {
      next('/')
    }
    return
  }

  // Handle tenant-specific routes
  if (to.params.tenantSlug && typeof to.params.tenantSlug === 'string') {
    const slug = to.params.tenantSlug
    // Already have this tenant in store (e.g. just set from login / my-tenants)
    if (tenantStore.hasTenant && tenantStore.tenantSlug === slug) {
      next()
      return
    }
    try {
      // Acotado: ver nota sobre withBootDeadline en la rama de inicialización
      // de arriba. Sin él, un /:tenantSlug podían bloquearse 30s+ por intento.
      await withBootDeadline(tenantStore.initializeFromSlug(slug), BOOT_DEADLINE_MS, 'tenant')
    } catch (error) {
      console.error('Failed to load tenant:', error)
      // If we have the tenant in available list, use it so post-login redirect doesn't bounce back
      const fromList = tenantStore.availableTenants?.find((t: { slug: string }) => t.slug === slug)
      if (fromList) {
        tenantStore.setCurrentTenant(fromList)
        next()
        return
      }
      // Transient failure (network/5xx) with a valid token: DON'T eject the
      // session to /login — the /login branch destroys the session via
      // logout() on a second consecutive blip. Views render their own
      // error/retry states and the next navigation retries the slug load.
      if (authStore.token && isTransientApiError(error)) {
        next()
        return
      }
      // A stale URL like /dashboard (no tenant prefix) matches this route with
      // slug='dashboard', which can never resolve. If the user still has their
      // persisted company, go straight to it instead of bouncing through
      // /login (which re-runs initialize() and logs a 404 from by-slug).
      if (authStore.isAuthenticated && tenantStore.hasTenant && tenantStore.tenantSlug) {
        next({ path: `/${tenantStore.tenantSlug}` })
        return
      }
      next('/login')
      return
    }
  }

  // Feature-gate: hide modules the tenant doesn't have (plan §3, D11).
  // hasFeature is permissive — only an explicit server `false` hides. Baseline
  // modules (invoicing/inventory/reports/payroll) are always true, so this never
  // blocks them. Fail-open when the tenant/features haven't loaded yet.
  const requiredFeature = to.meta.feature as string | undefined
  if (requiredFeature && tenantStore.hasTenant && !tenantStore.hasFeature(requiredFeature)) {
    const tenantSlug = to.params.tenantSlug as string
    next(tenantSlug ? { name: 'Dashboard', params: { tenantSlug } } : '/')
    return
  }

  // Redirect authenticated users away from login
  if (to.path === '/login' && authStore.isAuthenticated) {
    // Redirect to tenant dashboard or first available tenant
    if (tenantStore.hasTenant) {
      next(`/${tenantStore.tenantSlug}`)
    } else {
      // Fetch available tenants and redirect to first one
      try {
        const tenants = await withBootDeadline(
          tenantStore.fetchAvailableTenants(),
          BOOT_DEADLINE_MS,
          'tenants'
        )
        if (tenants.length > 0) {
          next(`/${tenants[0].slug}`)
        } else {
          // No tenants available, logout
          await authStore.logout()
          next('/login')
        }
      } catch (error) {
        console.error('Failed to fetch tenants:', error)
        // Transient blip with a valid token: keep the session alive and stay
        // on /login (the auth store's background retry ladder recovers it)
        // instead of logging out — two consecutive blips used to destroy a
        // perfectly valid session.
        if (authStore.token && isTransientApiError(error)) {
          next('/login')
          return
        }
        await authStore.logout()
        next('/login')
      }
    }
    return
  }

  next()
})

// Rutas `:param?` de componente único (listado + form + detalle en la misma
// vista) heredan el título del listado en el tab. Resuelve el título según el
// modo real: /received-payments (c281/c285) y /estimates (c286).
function resolveRouteTitle(to: RouteLocationGeneric): string {
  // List views that mount their create form in-place via ?action=create
  // (the /new and /create paths redirect here) must not keep the list title.
  if (to.query.action === 'create' && typeof to.meta.createTitle === 'string') {
    return to.meta.createTitle
  }
  if (to.name === 'ReceivedPaymentDetail') {
    if (to.params.paymentId === 'new') return 'Nuevo Pago Recibido'
    if (!to.params.paymentId) return 'Pagos Recibidos'
  }
  if (to.name === 'EstimateDetail') {
    const estimateId = to.params.estimateId
    if (estimateId === 'new') return 'Nueva Estimación'
    if (estimateId && to.query.action === 'edit') return 'Editar Estimación'
    if (estimateId) return 'Detalle de Estimación'
  }
  return to.meta.title as string
}

// Update document title based on route
router.afterEach((to) => {
  const baseTitle = 'Numera'
  const tenantStore = useTenantStore()

  let title = baseTitle

  if (to.meta.title) {
    const routeTitle = resolveRouteTitle(to)
    title = `${routeTitle} - ${baseTitle}`

    // Add tenant name only behind auth. On public pages (signup, login, password
    // recovery) a stale store still held the previous tenant's name, so a fresh
    // visitor saw someone else's company in the tab title.
    if (to.meta.requiresAuth !== false && tenantStore.tenantName) {
      title = `${routeTitle} - ${tenantStore.tenantName} - ${baseTitle}`
    }
  }

  document.title = title
})

export { routes, resolveRouteTitle }
export default router
